import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'

import { SvgIcon } from '~src/new/shared/ui/SvgIcon/SvgIcon'
import cls from './bottom-bar.module.scss'
import MaterialIcon from '~src/new/shared/ui/MaterialIcon/MaterialIcon'
import { Link } from 'react-router-dom'
import { useModalSheet } from '@/src/app/providers/ModalSheetProvider/lib/useModalSheet'

const BottomBarIcon = ({ label, icon, specialIcon }) => {
  if (specialIcon) {
    return <SvgIcon icon={specialIcon} name={label} size={32} className={cls.icon} />
  }
  return <MaterialIcon name={icon} size={32} className={cls.icon} ariaLabel={label} isSymbol />
}

const normalizePath = value => {
  if (!value) return ''
  return value.endsWith('/') && value !== '/' ? value.slice(0, -1) : value
}

const isActiveItem = (currentPath, link) => {
  const normalizedCurrent = normalizePath(currentPath)
  const normalizedLink = normalizePath(link)
  const isRootLink = normalizedLink === '/'
  return (
    normalizedCurrent === normalizedLink ||
    (!isRootLink && normalizedCurrent?.startsWith(`${normalizedLink}/`)) ||
    (normalizedLink === '/dashboard' && normalizedCurrent === '/')
  )
}

/**
 * Нижний навигационный бар для мобильных и десктопных страниц.
 * Рендерит список пунктов с иконками, подсвечивает активный пункт по пути.
 */
export const BottomBar = ({ items = [], activePath, className, style }) => {
  const barRef = useRef(null)
  const itemRefs = useRef([])
  const [activeRect, setActiveRect] = useState(null)
  const { isFullScreenOpen } = useModalSheet()

  const currentPath = activePath ?? (typeof window !== 'undefined' ? window.location?.pathname : '')

  const activeIndex = items.findIndex(item => isActiveItem(currentPath, item.to))

  const updateActiveRect = useCallback(() => {
    const barEl = barRef.current
    const itemEl = itemRefs.current[activeIndex]
    if (!barEl || !(itemEl instanceof Element)) {
      setActiveRect(null)
      return
    }

    const barRect = barEl.getBoundingClientRect()
    const itemRect = itemEl.getBoundingClientRect()
    const rawLeft = itemRect.left - barRect.left
    const rawTop = itemRect.top - barRect.top
    const rawRight = itemRect.right - barRect.left
    const maxRight = barRect.width
    const clampedLeft = Math.max(0, rawLeft)
    const clampedRight = Math.min(maxRight, rawRight)
    const clampedWidth = Math.max(0, clampedRight - clampedLeft)

    setActiveRect({
      left: clampedLeft,
      top: Math.max(0, rawTop),
      width: clampedWidth,
      height: itemRect.height,
    })
  }, [activeIndex])

  useLayoutEffect(() => {
    updateActiveRect()
  }, [updateActiveRect, items.length])

  useEffect(() => {
    const barEl = barRef.current
    const itemEl = itemRefs.current[activeIndex]
    if (!barEl || !(itemEl instanceof Element)) return undefined

    let frameId
    const scheduleUpdate = () => {
      if (frameId) cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(updateActiveRect)
    }

    scheduleUpdate()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(scheduleUpdate)
      observer.observe(barEl)
      observer.observe(itemEl)
      window.addEventListener('resize', scheduleUpdate)
      return () => {
        if (frameId) cancelAnimationFrame(frameId)
        observer.disconnect()
        window.removeEventListener('resize', scheduleUpdate)
      }
    }

    window.addEventListener('resize', scheduleUpdate)
    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      window.removeEventListener('resize', scheduleUpdate)
    }
  }, [activeIndex, updateActiveRect])

  if (!items?.length) return null

  return (
    <div id="bottom-bar" className={classNames(cls.container, className)} style={style}>
      <div
        className={classNames(cls.bar, {
          [cls.barBlocked]: isFullScreenOpen,
        })}
        ref={barRef}
      >
        <AnimatePresence initial={false}>
          {activeRect && (
            <motion.span
              className={cls.activeBg}
              initial={false}
              animate={{
                opacity: 1,
                left: activeRect.left,
                top: activeRect.top,
                width: activeRect.width,
                height: activeRect.height,
              }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'spring',
                stiffness: 190,
                damping: 30,
                mass: 1,
              }}
            />
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {items.map(({ icon, label, to, badge, specialIcon }, index) => {
            const key = to || `${label}-${index}`
            const isActive = isActiveItem(currentPath, to)

            return (
              <Link
                key={key}
                to={to}
                className={classNames(cls.item, { [cls.itemActive]: isActive })}
              >
                <span
                  className={cls.itemInner}
                  ref={el => {
                    itemRefs.current[index] = el
                  }}
                >
                  <span className={cls.content}>
                    <span className={cls.iconWrapper}>
                      <BottomBarIcon label={label} icon={icon} specialIcon={specialIcon} />
                      {!!badge && <span className={cls.badge}>{badge}</span>}
                    </span>

                    {isActive && label ? (
                      <motion.span
                        key="label"
                        className={cls.label}
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{
                          opacity: 0,
                          width: 0,
                          transition: { duration: 0.1, ease: 'easeIn' },
                        }}
                        transition={{ duration: 0.2, ease: 'easeIn' }}
                      >
                        {label}
                      </motion.span>
                    ) : null}
                  </span>
                </span>
              </Link>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}

BottomBar.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  activePath: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType, PropTypes.element]),
      title: PropTypes.string,
      link: PropTypes.string,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
}
